import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createFacility } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { 
  ArrowLeft,
  MapPin, 
  Building2, 
  Wifi, 
  Car,
  FileText,
  Tag,
  Image,
  Upload,
  Save,
  Eye,
  AlertCircle,
  Coffee,
  Monitor,
  Users,
  Clock,
  Calendar,
  Settings,
  CheckCircle,
  HelpCircle
} from 'lucide-react';
import { DashboardLayout } from '../../../client/components/Sidebar';
import { useLocationContext } from '../../../client/contexts/LocationContext';
import { locationSectionPathFromLocation } from '../../../client/lib/locationPaths';

// Tooltip component (reusable)
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface FacilityFormData {
  title: string;
  description: string;
  contentBody: string;
  category: string;
  facilityType: string;
  tags: string;
  images: string[];
  visibility: 'PUBLIC' | 'VERIFIED' | 'SUBSCRIBER';
  isPinned: boolean;
  requiresApproval: boolean;
  expiryDate: string;
  isAvailable: boolean;
  reservationRequired: boolean;
  operatingHours: string;
  location: string;
  contact: string;
  isActive: boolean;
}

interface FacilityType {
  value: string;
  label: string;
  icon: React.ComponentType<any>;
  description: string;
  canReserve: boolean;
  color: string;
}

const FACILITY_TYPES: FacilityType[] = [
  {
    value: 'vending',
    label: 'Vending Machine',
    icon: Coffee,
    description: 'Snack and beverage vending machines',
    canReserve: false,
    color: 'bg-orange-50 border-orange-200 hover:bg-orange-100'
  },
  {
    value: 'equipment',
    label: 'Equipment',
    icon: Monitor,
    description: 'Computers, printers, projectors, and other equipment',
    canReserve: true,
    color: 'bg-blue-50 border-blue-200 hover:bg-blue-100'
  },
  {
    value: 'rooms',
    label: 'Meeting Room',
    icon: Building2,
    description: 'Conference rooms, meeting spaces, and private rooms',
    canReserve: true,
    color: 'bg-purple-50 border-purple-200 hover:bg-purple-100'
  },
  {
    value: 'coworking',
    label: 'Co-Working Space',
    icon: Users,
    description: 'Shared workspaces and collaborative areas',
    canReserve: true,
    color: 'bg-green-50 border-green-200 hover:bg-green-100'
  },
  {
    value: 'parking',
    label: 'Parking Space',
    icon: Car,
    description: 'Parking spots and vehicle storage',
    canReserve: true,
    color: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'
  },
  {
    value: 'wifi',
    label: 'WiFi Hotspot',
    icon: Wifi,
    description: 'Internet access points and connectivity areas',
    canReserve: false,
    color: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100'
  }
];

const FACILITY_CATEGORIES = [
  { value: 'vending', label: 'Vending Machines', icon: Coffee },
  { value: 'equipment', label: 'Equipment', icon: Monitor },
  { value: 'rooms', label: 'Rooms', icon: Building2 },
  { value: 'coworking', label: 'Co-Working Spaces', icon: Users },
  { value: 'parking', label: 'Parking', icon: Car },
  { value: 'wifi', label: 'WiFi', icon: Wifi },
  { value: 'general', label: 'General', icon: MapPin }
];

const FACILITY_ACCESS_TYPES = [
  { value: 'available', label: 'Available', description: 'Freely available for use' },
  { value: 'reservable', label: 'Reservable', description: 'Requires advance reservation' },
  { value: 'walkin', label: 'Walk-in Only', description: 'First come, first served' }
];


export default function AddFacilityPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Get the selected facility type from URL params
  const selectedTypeParam = searchParams.get('type');
  const [selectedFacilityType, setSelectedFacilityType] = useState<FacilityType | null>(null);
  
  const [formData, setFormData] = useState<FacilityFormData>({
    title: '',
    description: '',
    contentBody: '',
    category: 'general',
    facilityType: 'available',
    tags: '',
    images: [],
    visibility: 'VERIFIED',
    isPinned: false,
    requiresApproval: false,
    expiryDate: '',
    isAvailable: true,
    reservationRequired: false,
    operatingHours: '',
    location: '',
    contact: '',
    isActive: true
  });

  // Initialize selected facility type from URL params
  useEffect(() => {
    if (selectedTypeParam) {
      const type = FACILITY_TYPES.find(t => t.value === selectedTypeParam);
      if (type) {
        setSelectedFacilityType(type);
        setFormData(prev => ({
          ...prev,
          category: type.value,
          reservationRequired: type.canReserve
        }));
      }
    }
  }, [selectedTypeParam]);

  // Helper function to get placeholder text based on facility type
  const getPlaceholderForType = (type: string): string => {
    switch (type) {
      case 'vending':
        return 'e.g., Snack Vending Machine - Level 2, Coffee Machine - Lobby';
      case 'equipment':
        return 'e.g., 3D Printer - Engineering Lab, Projector - Conference Room A';
      case 'rooms':
        return 'e.g., Conference Room A - Level 3, Meeting Room B - Level 1';
      case 'coworking':
        return 'e.g., Open Workspace - Level 2, Quiet Zone - Level 4';
      case 'parking':
        return 'e.g., Parking Spot #15 - Level B1, Visitor Parking - Ground Floor';
      case 'wifi':
        return 'e.g., WiFi Hotspot - Lobby, Guest Network - Level 2';
      default:
        return 'e.g., Facility Name - Location';
    }
  };

  const handleChange = (field: keyof FacilityFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!selectedLocation?.id) {
      newErrors.location = 'No location selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const facilityData = {
        locationId: selectedLocation!.id,
        title: formData.title.trim(),
        description: formData.description.trim(),
        contentBody: formData.contentBody.trim(),
        visibility: formData.visibility,
        category: formData.category,
        facilityType: formData.facilityType,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        images: formData.images,
        isPinned: formData.isPinned,
        requiresApproval: formData.requiresApproval,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate) : undefined,
        metadata: {
          facilityType: formData.facilityType,
          isAvailable: formData.isAvailable,
          reservationRequired: formData.reservationRequired,
          operatingHours: formData.operatingHours,
          facilityLocation: formData.location,
          contact: formData.contact,
          isActive: formData.isActive
        }
      };

      await createFacility(facilityData);
      
      navigate(locationSectionPathFromLocation(selectedLocation, 'facilities'));
    } catch (error) {
      console.error('Error creating facility:', error);
      setErrors({ submit: 'Failed to create facility. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTypeSelection = (type: FacilityType) => {
    setSelectedFacilityType(type);
    setFormData(prev => ({
      ...prev,
      category: type.value,
      reservationRequired: type.canReserve
    }));
  };

  const handleCancel = () => {
    navigate(locationSectionPathFromLocation(selectedLocation, 'facilities'));
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
            <p className="text-gray-600">Please log in to add a facility.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Location Selected</h2>
            <p className="text-gray-600">Please select a location to add a facility.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // If no facility type is selected, show type selection
  if (!selectedFacilityType) {
    return (
      <DashboardLayout>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-8 h-8 text-teal-600" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Add New Facility</h1>
                  <p className="text-gray-600">Location: {selectedLocation.name}</p>
                </div>
              </div>
              
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Facilities
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-blue-900 mb-2">Select Facility Type</h2>
              <p className="text-blue-800">
                Choose the type of facility you want to add. Each type has different features and reservation capabilities.
              </p>
            </div>
          </div>

          {/* Facility Type Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FACILITY_TYPES.map((type) => {
              const IconComponent = type.icon;
              return (
                <button
                  key={type.value}
                  onClick={() => handleTypeSelection(type)}
                  className={`${type.color} border-2 rounded-xl p-6 text-left transition-all duration-200 hover:shadow-lg hover:scale-105 group`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white rounded-lg shadow-sm group-hover:shadow-md transition-shadow">
                      <IconComponent className="w-6 h-6 text-gray-700" />
                    </div>
                    {type.canReserve && (
                      <div className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        <Calendar className="w-3 h-3" />
                        Reservable
                      </div>
                    )}
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {type.label}
                  </h3>
                  
                  <p className="text-sm text-gray-600 mb-4">
                    {type.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      {type.canReserve ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Can be reserved</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-4 h-4 text-orange-500" />
                          <span>Walk-in only</span>
                        </>
                      )}
                    </div>
                    <div className="text-blue-600 group-hover:text-blue-700 font-medium">
                      Select →
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Help Text */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Need help choosing? Contact your location administrator for guidance on facility types and requirements.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // If facility type is selected, show the form
  return (
    <DashboardLayout>
      <div className="flex h-full">
        {/* Main Content Area - Takes full width excluding left sidebar */}
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className="flex-1 p-6">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Add {selectedFacilityType.label}</h1>
                    <p className="text-gray-600">Location: {selectedLocation?.name || 'No location selected'}</p>
                  </div>
                </div>
              </div>

              <form id="facility-form" onSubmit={handleSubmit}>
                {/* Full-width sections */}
                <div className="space-y-6 mb-6">
                  {/* Primary Input Field */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">What {selectedFacilityType.label.toLowerCase()} are you adding?</h3>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.title ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder={getPlaceholderForType(selectedFacilityType.value)}
                      required
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.title}
                      </p>
                    )}
                  </div>

                  {/* Description Field */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.description ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder="Brief description of the facility, its purpose, and key features..."
                      required
                    />
                    {errors.description && (
                      <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.description}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">0/1000 characters</p>
                  </div>
                </div>

                {/* Two-column layout for form fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Category/Type Fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Facility Type</label>
                        <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                          {selectedFacilityType.label}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Access Type</label>
                        <select
                          value={formData.facilityType}
                          onChange={(e) => handleChange('facilityType', e.target.value)}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        >
                          {FACILITY_ACCESS_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Operating Hours */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Operating Hours</label>
                      <input
                        type="text"
                        value={formData.operatingHours}
                        onChange={(e) => handleChange('operatingHours', e.target.value)}
                        className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        placeholder="e.g., Mon-Fri: 6:00 AM - 10:00 PM, 24/7"
                      />
                    </div>

                    {/* Contact Information */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contact Information</label>
                      <input
                        type="text"
                        value={formData.contact}
                        onChange={(e) => handleChange('contact', e.target.value)}
                        className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        placeholder="Phone, email, or person to contact"
                      />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Tags Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center gap-2">
                          <span>Tags</span>
                          <Tooltip content="Separate tags with commas">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <input
                        type="text"
                        value={formData.tags}
                        onChange={(e) => handleChange('tags', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., printing, snacks, meeting room, wifi (separate with commas)"
                      />
                    </div>

                    {/* File Upload Section */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Images</h3>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        <div className="text-center">
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 mb-2">Upload images to support your facility</p>
                          <p className="text-sm text-gray-400">PNG, JPG up to 10MB each</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error message */}
                {errors.submit && (
                  <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}
              </form>
            </div>

            {/* Right Sidebar - Anchored to the right */}
            <div className="w-80 border-l border-border flex flex-col">
              {/* Action Buttons */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="facility-form"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !formData.title.trim()}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Adding...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Add Facility
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Approval Notice */}
              <div className="p-6 border-b border-border">
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>All postings go through an approval process before being published.</span>
                </div>
              </div>

              {/* Additional Options */}
              <div className="p-6 border-b border-border">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Options</h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isAvailable}
                      onChange={(e) => handleChange('isAvailable', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">Currently Available</div>
                      <Tooltip content="Facility is currently available for use">
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.reservationRequired}
                      onChange={(e) => handleChange('reservationRequired', e.target.checked)}
                      disabled={!selectedFacilityType.canReserve}
                      className={`w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 ${
                        !selectedFacilityType.canReserve ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    />
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">Reservation Required</div>
                      <Tooltip content={selectedFacilityType.canReserve ? "Users must reserve this facility in advance" : "This facility type cannot be reserved"}>
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isPinned}
                      onChange={(e) => handleChange('isPinned', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">Pin to Top</div>
                      <Tooltip content="This facility will appear at the top of the list">
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.requiresApproval}
                      onChange={(e) => handleChange('requiresApproval', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">Requires Approval</div>
                      <Tooltip content="This facility requires admin approval before being visible">
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleChange('isActive', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">Active</div>
                      <Tooltip content="Facility is active and visible to users">
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                  </label>
                </div>
              </div>

              {/* Additional Fields Section */}
              <div className="p-6 flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Location Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specific Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleChange('location', e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent"
                      placeholder="e.g., Level 2 Main Atrium, Engineering Lab"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date (Optional)</label>
                    <input
                      type="date"
                      value={formData.expiryDate}
                      onChange={(e) => handleChange('expiryDate', e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent"
                    />
                    <p className="text-sm text-gray-500 mt-1">When should this facility listing expire?</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}


